import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { Alert, Grid, Paper, Typography } from '@mui/material';
import { getVideoCase } from '../api';
import { useEnv } from '../contexts/EnvContext';
import PageHeader from './common/PageHeader';
import LoadingBlock from './common/LoadingBlock';
import ErrorBlock from './common/ErrorBlock';
import CallTimeline from './CallTimeline';
import RecordingList from './RecordingList';
import SettleCase from './SettleCase';
import { CASE_KINDS } from './VideoCases';
import { Fact, Rows } from './VideoBooking';

const when = (d) => (d ? dayjs(d).format('MMM D, YYYY h:mm A') : '—');
const text = (v) => (v == null || v === '' ? '—' : String(v));
const HISTORY_WORDS = {
  case_opened: 'Opened',
  case_settled: 'Settled',
  recording_viewed: 'Recording viewed',
  recording_removed: 'Recording removed',
};

/**
 * One support case in full for staff (admin panel plan RPT-16, RPT-17):
 * Bidsy's route 43, read fresh each time. Account steps stay on the user
 * pages ("Send SMS", "Remove from Browse", "Delete user").
 */
export default function VideoCase() {
  const { caseId } = useParams();
  const location = useLocation();
  const { env } = useEnv();
  const from = location.state?.from || { label: 'Video cases', path: '/video-cases' };
  const [view, setView] = useState({ loading: true, data: null, error: null, notice: null });
  const latest = useRef(0);

  const load = useCallback(async ({ quiet = false } = {}) => {
    const asked = ++latest.current;
    if (!quiet) setView({ loading: true, data: null, error: null, notice: null });
    try {
      const answer = await getVideoCase(caseId);
      if (asked !== latest.current) return;
      setView({ loading: false, data: answer.data, error: null, notice: null });
    } catch (e) {
      if (asked !== latest.current) return;
      const body = e?.response?.data;
      if (e?.response?.status === 404) setView({ loading: false, data: null, error: null, notice: body?.message || 'No video case has this number.' });
      else setView({ loading: false, data: null, error: body?.message || e?.message || 'Could not load the case', notice: null });
    }
  }, [caseId]);

  useEffect(() => { load(); }, [load, env]);

  const header = <PageHeader title={`Video case #${caseId}`} subtitle={<Link to={from.path}>{from.label}</Link>} />;
  if (view.loading) return <>{header}<LoadingBlock height={320} /></>;
  if (view.error) return <>{header}<ErrorBlock message={view.error} onRetry={load} /></>;
  if (view.notice) return <>{header}<Alert severity="info">{view.notice}</Alert></>;

  const { case: kase, booking, fan, creator, call } = view.data;
  const here = { label: `Video case #${kase.id}`, path: `/video-cases/${kase.id}` };
  const person = (p) => (p ? <Link to={`/user/${p.id}`} state={{ from: here }}>{p.username}</Link> : '—');
  const nameOf = (id) => (Number(id) === Number(fan?.id) ? fan.username : Number(id) === Number(creator?.id) ? creator.username : `User #${id}`);

  return (
    <>
      {header}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2}>
          <Fact label="Kind">{CASE_KINDS[kase.kind] || kase.kind}</Fact>
          <Fact label="Cause">{text(kase.cause)}</Fact>
          <Fact label="State">{kase.state === 'settled' ? `Settled: ${kase.outcome}${kase.applied_code ? ` (${kase.applied_code})` : ''}` : 'Open'}</Fact>
          <Fact label="Fan">{person(fan)}</Fact>
          <Fact label="Creator">{person(creator)}</Fact>
          <Fact label="Booking">
            <Link to={`/video-bookings/${booking.id}`} state={{ from: here }}>Video call #{booking.id}</Link>
          </Fact>
          <Fact label="Opened">{when(kase.opened_at)}</Fact>
          <Fact label="Reported by">{kase.reporter_side || 'Nobody: the call failed'}</Fact>
          <Fact label="Holds">{Number(kase.tokens_held || 0).toLocaleString()} tokens</Fact>
          {kase.reason_code ? (
            <Fact label={kase.kind === 'post_call_report' ? 'Category' : 'Reason'}>
              {text(kase.reason_label)}
              {kase.reason_note ? ` · “${kase.reason_note}”` : ''}
            </Fact>
          ) : null}
          {kase.state === 'settled' ? (
            <Fact label="Settled by">{`${kase.settled_by}, ${when(kase.settled_at)}`}{kase.staff_note ? ` · note: “${kase.staff_note}”` : ''}</Fact>
          ) : null}
        </Grid>
      </Paper>

      {/* Kept mounted once the case is settled, so the answer of a settle just made stays on screen. */}
      <SettleCase
        kase={kase}
        fan={fan}
        creator={creator}
        outcomes={view.data.allowed_outcomes || []}
        settled={kase.state !== 'open'}
        onSettled={() => load({ quiet: true })}
      />

      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
          The call{call ? `: ${when(call.starts_at)}, ${call.call_length_minutes} minutes, ${text(call.state)}${call.ending_code ? `, ${call.ending_code}` : ''}` : ''}
        </Typography>
        {call ? <CallTimeline timeline={call.timeline} totals={call.totals} /> : <Typography variant="body2">No call.</Typography>}
      </Paper>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>Recordings</Typography>
        <RecordingList recordings={call?.recordings || []} call={call} fan={fan} creator={creator} />
      </Paper>

      <Rows
        title="Joining the call"
        rows={call?.join_attempts || []}
        empty="No join attempt recorded."
        columns={[
          ['Who', (a) => nameOf(a.user_id)],
          ['When', (a) => when(a.requested_at)],
          ['Bidsy', (a) => `${text(a.outcome)}${a.refusal_code ? ` · ${a.refusal_code}` : ''}${a.server_error ? ` · ${a.server_error}` : ''}`],
          ['The browser said (its own word)', (a) => (a.browser_reported_at ? `${text(a.browser_outcome)}: ${text(a.browser_detail)}` : '—')],
        ]}
      />
      <Rows
        title="Held rating"
        rows={view.data.held_ratings || []}
        empty="No rating held for this case."
        columns={[
          ['By', (r) => nameOf(r.rater_id)],
          ['Of', (r) => nameOf(r.rated_id)],
          ['Stars', (r) => r.stars],
          ['Comment', (r) => text(r.comment)],
        ]}
      />
      <Rows
        title="Consent"
        rows={view.data.consents || []}
        empty="No consent recorded."
        columns={[
          ['Side', (c) => c.side],
          ['Rules version', (c) => text(c.rules_version || c.rule_version)],
          ['Agreed', (c) => when(c.agreed_at)],
        ]}
      />
      <Rows
        title="History"
        rows={(view.data.history || []).map((row, index) => ({ ...row, id: row.id || `h${index}` }))}
        empty="Nothing yet."
        columns={[
          ['When', (h) => when(h.created_at)],
          ['What', (h) => HISTORY_WORDS[h.action] || h.action],
          ['By', (h) => text(h.actor)],
          ['Note', (h) => text(h.note)],
        ]}
      />
    </>
  );
}
